import { Card, CardContent } from "@/components/ui/card";
import { Brain, Cpu, Target, TrendingUp } from "lucide-react";
import { motion, type Variants } from "framer-motion";

const pillars = [
  {
    icon: Brain,
    title: "Inteligência Híbrida",
    description:
      "A união entre visão estratégica humana e agentes de IA autônomos para acelerar processos e tomadas de decisão.",
    delay: 0.1,
  },
  {
    icon: Cpu,
    title: "Engenharia & Automações",
    description:
      "Sistemas proprietários, chatbots cognitivos e integrações que eliminam gargalos operacionais.",
    delay: 0.2,
  },
  {
    icon: Target,
    title: "Marketing de Alta Conversão",
    description:
      "Copywriting persuasivo, design de impacto e tráfego pago focado em aquisição contínua de clientes.",
    delay: 0.3,
  },
  {
    icon: TrendingUp,
    title: "Ecossistema Integrado",
    description:
      "Da captação do lead ao fechamento no PDV e controle de fluxo de caixa em uma estrutura unificada.",
    delay: 0.4,
  },
];

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const About = () => {
  return (
    <section id="sobre" className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto space-y-16">
          {/* Manifesto & Missão */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center space-y-6"
          >
            <span className="inline-block text-primary font-medium text-sm tracking-widest uppercase">
              Quem somos
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-foreground text-balance">
              Sobre a{" "}
              <span className="bg-hero-gradient bg-clip-text text-transparent">
                Abillyting
              </span>
            </h2>
            <div className="w-20 h-1 bg-hero-gradient mx-auto rounded-full" />

            <div className="max-w-4xl mx-auto text-center flex flex-col items-center pt-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-hero-gradient mb-6 shadow-lg shadow-primary/30 transition-transform duration-500 hover:scale-110 hover:rotate-6">
                <Brain className="text-primary-foreground" size={32} />
              </div>
              <h3 className="font-heading font-semibold text-2xl md:text-3xl mb-4 text-foreground">
                Nossa Missão
              </h3>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed text-balance">
                Nossa Missão: Unir o pensamento estratégico e criativo do marketing à precisão da
                engenharia de dados, automações e inteligência artificial. Criamos ecossistemas
                completos para marcas que buscam autoridade, conversão em escala e eficiência
                operacional.
              </p>
            </div>
          </motion.div>

          {/* Pilares */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {pillars.map((pillar) => (
              <motion.div key={pillar.title} variants={item}>
                <Card className="group h-full p-6 bg-card/60 backdrop-blur-xl border border-primary/10 rounded-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-primary/20 hover:border-primary/30">
                  <CardContent className="pt-6 space-y-4 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-hero-gradient text-primary-foreground shadow-lg shadow-primary/30 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                      <pillar.icon size={28} />
                    </div>
                    <h4 className="font-heading font-semibold text-lg md:text-xl text-foreground">
                      {pillar.title}
                    </h4>
                    <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
